import { useCallback, useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";

const POLL_INTERVAL_MS = 5000;

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

const formatTs = (value) => {
    if (!value) return "Never";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const MachineSyncBar = ({ onSynced }) => {
    const { apiClient } = useGlobalAPIQueryContext();
    const privileges = secureLocalStorage.getItem("privileges") || [];
    const canResync = privileges.includes("machine_dashboard_resync");
    const [syncState, setSyncState] = useState(null);
    const [starting, setStarting] = useState(false);

    // Held in a ref so changing the callback does not restart the poll interval.
    const onSyncedRef = useRef(onSynced);
    useEffect(() => {
        onSyncedRef.current = onSynced;
    }, [onSynced]);

    const loadState = useCallback(async () => {
        const response = await apiClient.get("/api/machine-plan/sync/");
        const data = unwrap(response) || null;
        setSyncState(data);
        return data;
    }, [apiClient]);

    useEffect(() => {
        loadState().catch(() => setSyncState(null));
    }, [loadState]);

    const isRunning = Boolean(syncState?.is_running);

    useEffect(() => {
        if (!isRunning) return undefined;
        const timer = setInterval(() => {
            loadState()
                .then((data) => {
                    if (data && !data.is_running) {
                        onSyncedRef.current?.();
                    }
                })
                .catch(() => {});
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [isRunning, loadState]);

    const resync = async () => {
        const confirm = await Swal.fire({
            title: "Re-sync machine data?",
            text: "This pulls the latest production counts and Elorca heartbeat for every machine. It runs in the background and may take a couple of minutes.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Re-sync",
        });
        if (!confirm.isConfirmed) return;
        setStarting(true);
        try {
            const response = await apiClient.post("/api/machine-plan/sync/", {});
            setSyncState(unwrap(response) || null);
        } catch (error) {
            if (error?.response?.status === 409) {
                await loadState().catch(() => {});
                Swal.fire(
                    "Already running",
                    "A sync is already in progress. The marker will update when it finishes.",
                    "info"
                );
            } else {
                Swal.fire(
                    "Re-sync failed",
                    error?.response?.data?.error || error.message,
                    "error"
                );
            }
        } finally {
            setStarting(false);
        }
    };

    const failed = syncState?.status === "failed";

    return (
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
            <div>
                <span className="text-soft">
                    Last data fetched at: {formatTs(syncState?.last_success_at)}
                </span>
                {isRunning ? (
                    <span className="badge bg-warning ms-2">Syncing…</span>
                ) : null}
                {failed && syncState?.last_error ? (
                    <span className="text-danger ms-2">
                        Last sync failed: {syncState.last_error}
                    </span>
                ) : null}
            </div>
            {canResync ? (
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={resync}
                    disabled={starting || isRunning}
                >
                    {isRunning ? "Syncing…" : starting ? "Starting…" : "Re-sync"}
                </button>
            ) : null}
        </div>
    );
};

export default MachineSyncBar;
